/**
 * Configuration for a shared external store backed by a single browser event listener.
 *
 * @param eventName          Window event that can change the snapshot (e.g. `'resize'`, `'scroll'`).
 * @param readSnapshot       Reads the current snapshot from the browser environment.
 *                           Must handle non-browser environments itself.
 * @param defaultSnapshot    Value served by `getServerSnapshot` when no DOM is available.
 * @param areSnapshotsEqual  Value-equality check between the cached and next snapshot.
 *                           Subscribers are only notified when this returns `false`.
 *                           Defaults to `Object.is`.
 */
export type ExternalStoreConfig<TSnapshot> = {
  eventName: string;
  readSnapshot: () => TSnapshot;
  defaultSnapshot: TSnapshot;
  areSnapshotsEqual?: (currentSnapshot: TSnapshot, nextSnapshot: TSnapshot) => boolean;
};

/**
 * The trio of functions `useSyncExternalStore` expects from an external store.
 */
export type ExternalStore<TSnapshot> = {
  subscribe: (subscriber: () => void) => () => void;
  getSnapshot: () => TSnapshot;
  getServerSnapshot: () => TSnapshot;
};

const NOOP_UNSUBSCRIBE = () => {};

/**
 * Creates a shared external store for browser state that changes in response to a window event.
 *
 * The store is the solution to the "one listener per mounted component" problem: instead of
 * every hook consumer attaching its own `window` listener and keeping local state, the first
 * mounted consumer starts a single browser-level listener and the last unmounted consumer
 * removes it. All consumers read the same cached snapshot through `useSyncExternalStore`.
 *
 * Behavior contract (shared by every store created here):
 *
 * - The cache is initialized eagerly when the store is created, so the very first render sees
 *   the real browser value — the same first-render semantics as `useState(window.innerWidth)`.
 * - The cache changes ONLY through paths that also notify subscribers (the browser event
 *   handler and the subscription-time re-sync below). This is the invariant that keeps every
 *   mounted consumer on the same value: a read must never advance the cache behind the other
 *   subscribers' backs, otherwise a viewport change that the event listener missed (a dropped
 *   or coalesced `resize` event, zoom, orientation change) would be picked up only by
 *   whichever component happens to re-render next, leaving the rest of the UI permanently
 *   stale at the old value.
 * - `getSnapshot` is therefore pure: it returns the cached snapshot without touching the
 *   browser. React calls it during render and during its own change detection, and it must be
 *   cheap, side-effect free, and referentially stable between real changes.
 * - The browser event can fire continuously (a resize fires on every pixel of a drag), but the
 *   equality gate means subscribers are only notified when the snapshot actually changes.
 * - `getServerSnapshot` never touches the browser, so React has a deterministic value when
 *   `window` is absent (SSR or non-DOM test environments).
 *
 * @param config See {@link ExternalStoreConfig}.
 * @returns The `subscribe` / `getSnapshot` / `getServerSnapshot` trio for `useSyncExternalStore`.
 */
export const createExternalStore = <TSnapshot>(config: ExternalStoreConfig<TSnapshot>): ExternalStore<TSnapshot> => {
  const {
    eventName,
    readSnapshot,
    defaultSnapshot,
    areSnapshotsEqual = Object.is,
  } = config;

  let snapshot = readSnapshot();
  let unsubscribeFromBrowser: (() => void) | undefined;

  const subscribers = new Set<() => void>();

  /**
   * Refreshes the cached snapshot from the browser and reports whether it changed.
   *
   * Every caller of this function must notify subscribers when it reports a change —
   * that pairing is what guarantees all consumers always observe the same snapshot.
   */
  const updateSnapshot = (): boolean => {
    const nextSnapshot = readSnapshot();

    if (areSnapshotsEqual(snapshot, nextSnapshot)) {
      return false;
    }

    snapshot = nextSnapshot;

    return true;
  };

  /**
   * Notifies every mounted consumer that React should read the snapshot again.
   *
   * The browser listener is global, but React still needs one callback per hook subscriber
   * so each mounted component can re-render when the shared snapshot changes.
   */
  const notifySubscribers = (): void => {
    subscribers.forEach((subscriber) => subscriber());
  };

  /**
   * Handles browser events by updating the snapshot before notifying.
   *
   * The equality gate means consumers only re-render when the event actually changed the
   * snapshot, no matter how often the browser fires it.
   */
  const handleBrowserChange = (): void => {
    if (updateSnapshot()) {
      notifySubscribers();
    }
  };

  /**
   * Starts the single browser-level listener used by all consumers of this store.
   *
   * Returns a no-op unsubscribe outside the browser so the subscription lifecycle stays
   * uniform: `subscribe` can always store and later call the returned cleanup.
   */
  const subscribeToBrowser = (): (() => void) => {
    if (typeof window === 'undefined') {
      return NOOP_UNSUBSCRIBE;
    }

    window.addEventListener(eventName, handleBrowserChange);

    return () => {
      window.removeEventListener(eventName, handleBrowserChange);
    };
  };

  /**
   * Registers a React subscriber with the shared store.
   *
   * The first subscriber starts the browser listener; the last unsubscribe removes it, so an
   * idle store holds no browser resources.
   *
   * Every subscription also re-syncs the cache and, when it changed, notifies all
   * subscribers — not just the new one. This heals viewport changes the listener could not
   * have seen (e.g. they happened while every consumer was unmounted and the listener was
   * detached) uniformly across the whole app instead of only for the mounting component.
   */
  const subscribe = (subscriber: () => void): (() => void) => {
    subscribers.add(subscriber);

    if (!unsubscribeFromBrowser) {
      unsubscribeFromBrowser = subscribeToBrowser();
    }

    if (updateSnapshot()) {
      notifySubscribers();
    }

    return () => {
      subscribers.delete(subscriber);

      if (!subscribers.size && unsubscribeFromBrowser) {
        unsubscribeFromBrowser();
        unsubscribeFromBrowser = undefined;
      }
    };
  };

  /**
   * Returns the cached snapshot React should use during rendering.
   *
   * Intentionally pure — see the factory-level contract. The cache is kept current by the
   * browser event handler and the subscription-time re-sync, never by this read.
   */
  const getSnapshot = (): TSnapshot => snapshot;

  /**
   * Returns the non-browser snapshot used when no DOM is available.
   */
  const getServerSnapshot = (): TSnapshot => defaultSnapshot;

  return { subscribe, getSnapshot, getServerSnapshot };
};
